import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemIcon,
  ListItemText,
  FormControlLabel,
  Checkbox
} from "@mui/material";

import DescriptionIcon from "@mui/icons-material/Description";
import { useUserProductAccess } from "./hooks/useUserProductAccess";
import ProjectTagField from "./projects/ProjectTagField";

export default function ReportProcessDialog({ isOpen, onClose, onProcess, selectedClones, setSelectedClones, availableClones, projectTags, defaultTag }) {
  const [selectedProduct, setSelectedProduct] = useState("");
  // Defaults to the tag the input file was uploaded with.
  const [projectTag, setProjectTag] = useState(defaultTag || null);
  useEffect(() => {
    if (isOpen) setProjectTag(defaultTag || null);
  }, [isOpen, defaultTag]);
  const [usePostProcessing, setUsePostProcessing] = useState(true);
  const [useBioInterpreter, setUseBioInterpreter] = useState(false);
  const [localSelectedClones, setLocalSelectedClones] = useState([]);
  const {
    isLoading,
    productRoles: products,
    hasBioInterpreter: showBioInterpreterCheckbox,
  } = useUserProductAccess({ enabled: isOpen });

  // Use availableClones for rendering, fallback to selectedClones
  const cloneLabels = availableClones && availableClones.length > 0 ? availableClones : selectedClones;

  // When dialog opens, initialize localSelectedClones from selectedClones
  useEffect(() => {
    if (isOpen) {
      setLocalSelectedClones(selectedClones);
      setUsePostProcessing(true);
    }
  }, [isOpen, selectedClones]);

  const handleCloneCheckboxChange = (label) => {
    setLocalSelectedClones((prev) =>
      prev.includes(label)
        ? prev.filter((l) => l !== label)
        : [...prev, label]
    );
  };

  const handleSelectAllClones = () => setLocalSelectedClones(cloneLabels);
  const handleDeselectAllClones = () => setLocalSelectedClones([]);

  const handlePostProcessingChange = (e) => {
    const checked = e.target.checked;
    setUsePostProcessing(checked);
    if (!checked) setUseBioInterpreter(false);
  };

  const handleBioInterpreterChange = (e) => {
    const checked = e.target.checked;
    setUseBioInterpreter(checked);
    if (checked) setUsePostProcessing(true);
  };

  // Conditions exist but none are checked: nothing to run (an empty selection
  // would otherwise mean "run everything").
  const noneSelected = cloneLabels.length > 0 && localSelectedClones.length === 0;

  const handleProcess = () => {
    if (selectedProduct && !noneSelected) {
      setSelectedClones(localSelectedClones); // propagate to parent
      onProcess(selectedProduct, usePostProcessing, useBioInterpreter, localSelectedClones, projectTag);
      onClose();
    }
  };

  const getColorFromHash = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return `hsl(${hash % 360}, 60%, 50%)`;
  };

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle>Select Product</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <CircularProgress />
        ) : (
          <>
            <FormControl fullWidth>
              <InputLabel>Product</InputLabel>
              <Select
                value={selectedProduct}
                onChange={(e) => setSelectedProduct(e.target.value)}
                renderValue={(selected) => {
                  const selectedProductObj = products.find(p => p.id === selected);
                  const color = selectedProductObj ? getColorFromHash(selectedProductObj.id) : "#ccc";
                  return (
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <DescriptionIcon style={{ color, fontSize: 24 }} />
                      {selectedProductObj?.title}
                    </div>
                  );
                }}
              >
                {products.map((product) => {
                  const color = getColorFromHash(product.id);
                  return (
                    <MenuItem key={product.id} value={product.id}>
                      <ListItemIcon>
                        <DescriptionIcon style={{ color, fontSize: 24 }} />
                      </ListItemIcon>
                      <ListItemText primary={product.title} />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            {/* Clone checkboxes below product dropdown */}
            <div style={{ margin: '16px 0' }}>
              <strong>Select Conditions:</strong>
              <div style={{ display: 'flex', gap: 8, margin: '8px 0' }}>
                <Button size="small" onClick={handleSelectAllClones}>Select All</Button>
                <Button size="small" onClick={handleDeselectAllClones}>Deselect All</Button>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8,
                  maxHeight: cloneLabels.length > 20 ? 200 : undefined,
                  overflowY: cloneLabels.length > 20 ? 'auto' : 'visible',
                  border: cloneLabels.length > 20 ? '1px solid #eee' : undefined,
                  padding: 4,
                  minWidth: 320,
                }}
              >
                {cloneLabels.map((label) => (
                  <FormControlLabel
                    key={label}
                    control={
                      <Checkbox
                        checked={localSelectedClones.includes(label)}
                        onChange={() => handleCloneCheckboxChange(label)}
                      />
                    }
                    label={label}
                  />
                ))}
              </div>
            </div>

            <FormControlLabel
              control={
                <Checkbox
                  checked={usePostProcessing}
                  onChange={handlePostProcessingChange}
                />
              }
              label="Use Post Processing"
            />

            <ProjectTagField
              value={projectTag}
              onChange={setProjectTag}
              tags={projectTags?.tags}
              available={projectTags?.available}
              staff={projectTags?.staff}
              projects={projectTags?.projects}
            />

            {showBioInterpreterCheckbox && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={useBioInterpreter}
                    onChange={handleBioInterpreterChange}
                    disabled={!usePostProcessing}
                  />
                }
                label="Use BioInterpreter"
              />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {noneSelected && (
          <span style={{ marginRight: "auto", paddingLeft: 16, fontSize: 13, color: "#B45309" }}>
            Select at least one condition to process.
          </span>
        )}
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleProcess}
          color="primary"
          variant="contained"
          disabled={!selectedProduct || noneSelected}
        >
          Process
        </Button>
      </DialogActions>
    </Dialog>
  );
}
